import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Power, 
  PowerOff, 
  Save, 
  Eye, 
  AlertCircle,
  Clock,
  Users,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import { useMaintenanceMode } from '../../hooks/useMaintenanceMode';
import OrderingSettingsCard from './OrderingSettingsCard';

const SystemSettings = () => {
  const { maintenanceMode, loading: hookLoading, refresh } = useMaintenanceMode();
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    enabled: false,
    message: '',
    scheduled_start: '',
    scheduled_end: '',
    allow_staff: false
  });

  // Update form when maintenance mode changes
  useEffect(() => {
    if (maintenanceMode) {
      setFormData({
        enabled: maintenanceMode.enabled || false,
        message: maintenanceMode.message || "We are currently performing scheduled maintenance. We'll be back shortly!",
        scheduled_start: maintenanceMode.scheduled_start || '',
        scheduled_end: maintenanceMode.scheduled_end || '',
        allow_staff: maintenanceMode.allow_staff || false
      });
    }
  }, [maintenanceMode]);

  const handleToggle = (newState) => {
    setPendingAction(newState);
    setShowConfirmDialog(true);
  };

  const confirmToggle = async () => {
    try {
      setLoading(true);
      setShowConfirmDialog(false);

      const { data, error } = await supabase
        .from('system_settings')
        .update({
          setting_value: {
            enabled: pendingAction,
            message: formData.message,
            scheduled_start: formData.scheduled_start || null,
            scheduled_end: formData.scheduled_end || null,
            allow_staff: formData.allow_staff
          },
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', 'maintenance_mode')
        .select()
        .single();

      if (error) throw error;

      showNotification(
        `Maintenance mode ${pendingAction ? 'enabled' : 'disabled'} successfully`,
        'success'
      );
      
      // Refresh the maintenance mode status
      refresh();
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      showError(error, 'Failed to update maintenance mode');
    } finally {
      setLoading(false);
      setPendingAction(null);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('system_settings')
        .update({
          setting_value: {
            enabled: formData.enabled,
            message: formData.message,
            scheduled_start: formData.scheduled_start || null,
            scheduled_end: formData.scheduled_end || null,
            allow_staff: formData.allow_staff
          },
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', 'maintenance_mode')
        .select()
        .single();

      if (error) throw error;

      showNotification('Settings saved successfully', 'success');
      refresh();
    } catch (error) {
      console.error('Error saving settings:', error);
      showError(error, 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = () => {
    return formData.enabled ? 'bg-red-500' : 'bg-green-500';
  };

  const getStatusText = () => {
    return formData.enabled ? 'Active' : 'Inactive';
  };

  if (hookLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Settings"
        description="Manage system-wide settings, online ordering and maintenance mode"
        actions={
          <Button
            variant="secondary"
            onClick={refresh}
            loading={loading}
            icon={<RefreshCw className="h-4 w-4" />}
          >
            Refresh
          </Button>
        }
      />

      {/* Online Ordering Toggle */}
      <OrderingSettingsCard />

      {/* Status Banner */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`${formData.enabled ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border rounded-lg p-6`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`${getStatusColor()} rounded-full p-3`}>
              {formData.enabled ? (
                <PowerOff className="h-6 w-6 text-white" />
              ) : (
                <Power className="h-6 w-6 text-white" />
              )}
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${formData.enabled ? 'text-red-900' : 'text-green-900'}`}>
                Maintenance Mode: {getStatusText()}
              </h3>
              <p className={`text-sm ${formData.enabled ? 'text-red-700' : 'text-green-700'}`}>
                {formData.enabled 
                  ? 'System is currently in maintenance mode. Only admins can access the portal.'
                  : 'System is operating normally. All users have access.'}
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            {!formData.enabled ? (
              <button
                onClick={() => handleToggle(true)}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                <PowerOff className="mr-2 h-4 w-4" />
                Enable Maintenance
              </button>
            ) : (
              <button
                onClick={() => handleToggle(false)}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <Power className="mr-2 h-4 w-4" />
                Disable Maintenance
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">
            Maintenance Mode Configuration
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Customize the maintenance mode message and settings
          </p>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
              Maintenance Message
            </label>
            <textarea
              id="message"
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand sm:text-sm"
              placeholder="Enter the message users will see during maintenance..."
            />
            <p className="mt-2 text-sm text-gray-500">
              This message will be displayed to users when maintenance mode is active.
            </p>
          </div>

          {/* Scheduled Maintenance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="scheduled_start" className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline h-4 w-4 mr-1" />
                Scheduled Start (Optional)
              </label>
              <input
                type="datetime-local"
                id="scheduled_start"
                value={formData.scheduled_start}
                onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value })}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="scheduled_end" className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="inline h-4 w-4 mr-1" />
                Scheduled End (Optional)
              </label>
              <input
                type="datetime-local"
                id="scheduled_end"
                value={formData.scheduled_end}
                onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value })}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-brand focus:border-brand sm:text-sm"
              />
            </div>
          </div>

          {/* Allow Staff Access */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="allow_staff"
                type="checkbox"
                checked={formData.allow_staff}
                onChange={(e) => setFormData({ ...formData, allow_staff: e.target.checked })}
                className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="allow_staff" className="font-medium text-gray-700">
                <Users className="inline h-4 w-4 mr-1" />
                Allow Staff Access
              </label>
              <p className="text-gray-500">
                If enabled, staff members will also be able to access the system during maintenance (in addition to admins).
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowPreview(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              <Eye className="mr-2 h-4 w-4" />
              Preview Page
            </button>

            <button
              onClick={handleSaveSettings}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-black bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
            >
              <Save className="mr-2 h-4 w-4" />
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 mb-2">Important Information</h4>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>System administrators always have access during maintenance mode</li>
              <li>Changes to maintenance mode are applied immediately</li>
              <li>All users will see the maintenance page in real-time when enabled</li>
              <li>The system will automatically check for scheduled maintenance windows</li>
              <li>All maintenance mode changes are logged for audit purposes</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${pendingAction ? 'bg-red-100' : 'bg-green-100'} sm:mx-0 sm:h-10 sm:w-10`}>
                    <AlertCircle className={`h-6 w-6 ${pendingAction ? 'text-red-600' : 'text-green-600'}`} />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {pendingAction ? 'Enable Maintenance Mode?' : 'Disable Maintenance Mode?'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {pendingAction 
                          ? 'This will immediately restrict access for all non-admin users. They will see the maintenance page until you disable maintenance mode.'
                          : 'This will immediately restore normal access for all users. The system will return to normal operation.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmToggle}
                  disabled={loading}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 ${
                    pendingAction 
                      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                      : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  }`}
                >
                  {loading ? 'Processing...' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setPendingAction(null);
                  }}
                  disabled={loading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={() => setShowPreview(false)}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Maintenance Page Preview
                  </h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                  <iframe
                    srcDoc={`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <style>
                            body { margin: 0; padding: 20px; font-family: system-ui; background: #1a1818; color: #fff; }
                            .container { max-width: 600px; margin: 0 auto; text-align: center; }
                            .message { background: #242222; padding: 20px; border-radius: 8px; margin: 20px 0; }
                          </style>
                        </head>
                        <body>
                          <div class="container">
                            <h1 style="color: #fed402;">Under Maintenance</h1>
                            <div class="message">
                              <p>${formData.message}</p>
                            </div>
                            ${formData.scheduled_end ? `<p style="color: #fed402;">Expected return: ${new Date(formData.scheduled_end).toLocaleString()}</p>` : ''}
                          </div>
                        </body>
                      </html>
                    `}
                    style={{ width: '100%', height: '400px', border: 'none' }}
                    title="Maintenance Page Preview"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemSettings;
